import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface Tag {
  id: string;
  projectId: string;
  categoryId: string;
  name: string;
  color: string;
  isNameEditable: boolean;
  numResources: number | null;
  assignees: unknown | null;
  parentId: string | null;
  status: string | null;
  users: unknown | null;
  roles: unknown | null;
  inverses?: Array<string | { id: string }>;
  exclusions?: Array<string | { id: string }>;
  children?: Array<string | { id: string }>;
  inverseObjects?: { id: string; name: string }[];
  exclusionObjects?: { id: string; name: string }[];
  childrenObjects?: { id: string; name: string }[];
}

export interface TagInput {
  name: string;
  color: string;
  categoryId: string;
  projectId: string;
  inverses: string[];
  exclusions: string[];
  children: string[];
}

export interface TagPage {
  pageNumber: number;
  totalPages: number;
  numberTags: number;
  totalTags: number;
  tags: Tag[];
}

export function useProjectTags(projectId: string, categoryId: string | null, page: number) {
  return useQuery<TagPage>({
    queryKey: ['projectTags', projectId, categoryId, page],
    queryFn: () =>
      api.get<TagPage>(`/project/${projectId}/tag?assignedOnly=false&categoryId=${categoryId}&page=${page}&limit=20`),
    enabled: !!projectId && !!categoryId,
    // Keep the current page's rows on screen while the next page loads so the
    // table doesn't blank out into the "no tags found" warning between fetches.
    placeholderData: keepPreviousData,
  });
}

export function useTagsByIds(projectId: string, ids: string[]) {
  return useQuery<Tag[]>({
    queryKey: ['tags', projectId, ids],
    queryFn: () => api.get<Tag[]>(`/project/${projectId}/tags/${ids.join(',')}`),
    enabled: !!projectId && ids.length > 0,
  });
}

export function useDeleteTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tag: { id: string; name: string }) => api.delete(`/project/${projectId}/tag/${tag.id}`),
    onSuccess: (_data, tag) => {
      notifySuccess({ title: 'Untagging Started', description: `Deleting tag ${tag.name}. We will notify you when it completes.`, timeout: 2500 });
      queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useCreateTag(projectId: string) {
  return useToastMutation<Tag, TagInput>(
    ['projectTags', projectId],
    (input) => api.post<Tag>(`/project/${projectId}/tag`, input),
    {
      success: (data, v) => ({ title: 'Tag added successfully', description: `Name: ${data?.name ?? v.name}`, timeout: 5000 }),
    },
  );
}

export function useReprocessTag(projectId: string) {
  return useToastMutation(
    ['projectTags', projectId],
    (tagId: string) => api.post(`/project/${projectId}/tag/${tagId}/reprocess`),
    {
      success: () => ({ title: 'Reprocessing started', description: '', timeout: 2500 }),
    },
  );
}

export function useUpdateTag(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<Tag, Error, Tag>({
    mutationFn: (input) => api.put<Tag>(`/project/${projectId}/tag/${input.id}`, input),
    onSuccess: (data, variables) => {
      notifySuccess({ title: 'Tag updated successfully', description: `Name: ${data?.name ?? variables.name}`, timeout: 5000 });
      queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

// Toast + refresh when tagging/untagging finishes (FeedService.TaggingComplete / UntaggingComplete).
// Mount once on the Tags tab.
export function useTaggingEventToasts(projectId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['projectTags', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    };
    const offTagging = onAngularEvent('FeedService.TaggingComplete', (message: { description: string }) => {
      notifySuccess({ title: 'Tagging complete', description: `${message.description} has completed.` });
      invalidate();
    });
    const offUntagging = onAngularEvent('FeedService.UntaggingComplete', (message: { description: string }) => {
      notifySuccess({ title: 'Untagging complete', description: `${message.description} has completed.` });
      invalidate();
    });
    return () => { offTagging(); offUntagging(); };
  }, [projectId, queryClient]);
}
